import { useState } from "react";
import { Link } from "react-router-dom";
import {
  TicketIcon, Search, Inbox, Clock, CheckCircle2, XCircle, AlertTriangle, Users, Filter,
} from "lucide-react";
import {
  useGetAdminTicketsQuery,
  useGetTicketStatsQuery,
  TicketStats,
} from "../api/supportApi";
import { StatusBadge, PriorityBadge } from "../components/TicketBadges";
import PageGate from "../../../components/PageGate";
import { PERMS } from "../../../utils/perms";

function fmt(d: string) {
  return new Date(d).toLocaleDateString("en-NG", { day: "numeric", month: "short", year: "numeric" });
}

const STATUS_TABS = [
  { value: "",            label: "All"         },
  { value: "open",        label: "Open"        },
  { value: "in_progress", label: "In Progress" },
  { value: "resolved",    label: "Resolved"    },
  { value: "closed",      label: "Closed"      },
];

const PRIORITY_TABS = [
  { value: "",       label: "All Priorities" },
  { value: "urgent", label: "Urgent"         },
  { value: "high",   label: "High"           },
  { value: "medium", label: "Medium"         },
  { value: "low",    label: "Low"            },
];

type StatCard = {
  label: string;
  val: number | string;
  icon: React.ElementType;
  iconCls: string;
};

function buildStatCards(stats?: TicketStats): StatCard[] {
  return [
    { label: "Open",        val: stats?.open        ?? "—", icon: Inbox,         iconCls: "bg-indigo-50 text-indigo-600"  },
    { label: "In Progress", val: stats?.in_progress  ?? "—", icon: Clock,         iconCls: "bg-amber-50 text-amber-600"   },
    { label: "Resolved",    val: stats?.resolved     ?? "—", icon: CheckCircle2,  iconCls: "bg-emerald-50 text-emerald-600"},
    { label: "Urgent",      val: stats?.urgent       ?? "—", icon: AlertTriangle, iconCls: "bg-red-50 text-red-600"       },
    { label: "Total",       val: stats?.total        ?? "—", icon: Users,         iconCls: "bg-gray-100 text-gray-600"    },
    { label: "Closed",      val: stats?.closed       ?? "—", icon: XCircle,       iconCls: "bg-gray-100 text-gray-400"    },
  ];
}

export default function AdminTicketsPage() {
  const [status,   setStatus]   = useState("");
  const [priority, setPriority] = useState("");
  const [search,   setSearch]   = useState("");
  const [page,     setPage]     = useState(1);

  const { data: result, isLoading } = useGetAdminTicketsQuery({
    status:   status   || undefined,
    priority: priority || undefined,
    search:   search   || undefined,
    page,
  });

  const { data: stats } = useGetTicketStatsQuery();

  const tickets    = result?.data        ?? [];
  const total      = result?.total       ?? 0;
  const perPage    = result?.per_page    ?? 20;
  const totalPages = Math.ceil(total / perPage);

  const statCards = buildStatCards(stats);

  return (
    <PageGate perm={PERMS.support.adminAccess}>
      <div className="min-h-screen bg-[#f8f9fa] p-4 md:p-8 space-y-6 pb-20">
        <div className="max-w-7xl mx-auto space-y-6">

          {/* Stats */}
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
            {statCards.map((s) => (
              <div key={s.label} className="bg-white border border-gray-100 p-4 rounded-3xl shadow-sm flex items-center gap-3">
                <div className={`w-10 h-10 rounded-2xl flex items-center justify-center shrink-0 ${s.iconCls}`}>
                  <s.icon className="w-5 h-5" />
                </div>
                <div>
                  <div className="text-[9px] font-black text-gray-400 uppercase tracking-widest leading-none">{s.label}</div>
                  <div className="text-xl font-black text-gray-900">{s.val}</div>
                </div>
              </div>
            ))}
          </div>

          {/* Header */}
          <div className="bg-white border border-gray-100 rounded-3xl p-6 shadow-sm flex items-center gap-4">
            <div className="w-14 h-14 rounded-2xl bg-gray-900 flex items-center justify-center text-white shadow-xl shadow-gray-900/20 shrink-0">
              <TicketIcon className="w-7 h-7" />
            </div>
            <div>
              <h1 className="text-2xl font-black text-gray-900 tracking-tight leading-none uppercase">Support Queue</h1>
              <p className="text-xs text-gray-400 mt-1">{total} ticket{total !== 1 ? "s" : ""} total</p>
            </div>
          </div>

          {/* Filters */}
          <div className="bg-white border border-gray-100 rounded-2xl p-5 shadow-sm space-y-3">
            <div className="flex items-center gap-2 text-[10px] font-black text-gray-400 uppercase tracking-widest">
              <Filter className="w-3.5 h-3.5" /> Filters
            </div>
            <div className="flex flex-col sm:flex-row gap-3">
              <div className="relative flex-1">
                <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <input
                  value={search}
                  onChange={(e) => { setSearch(e.target.value); setPage(1); }}
                  placeholder="Search subject, reference…"
                  className="w-full pl-10 pr-4 py-2.5 border border-gray-200 rounded-xl text-sm bg-white focus:outline-none focus:ring-2 focus:ring-gray-900 transition"
                />
              </div>
              <select
                value={priority}
                onChange={(e) => { setPriority(e.target.value); setPage(1); }}
                className="px-4 py-2.5 border border-gray-200 rounded-xl text-sm bg-white focus:outline-none focus:ring-2 focus:ring-gray-900 transition"
              >
                {PRIORITY_TABS.map((p) => (
                  <option key={p.value} value={p.value}>{p.label}</option>
                ))}
              </select>
            </div>
            <div className="flex gap-2 flex-wrap">
              {STATUS_TABS.map((tab) => (
                <button
                  key={tab.value}
                  onClick={() => { setStatus(tab.value); setPage(1); }}
                  className={`px-4 py-1.5 rounded-xl text-xs font-black transition-all ${
                    status === tab.value
                      ? "bg-gray-900 text-white shadow-lg shadow-gray-900/20"
                      : "bg-gray-50 border border-gray-200 text-gray-600 hover:bg-gray-100"
                  }`}
                >
                  {tab.label}
                </button>
              ))}
            </div>
          </div>

          {/* Table */}
          {isLoading ? (
            <div className="space-y-3">
              {[1, 2, 3, 4].map((i) => (
                <div key={i} className="h-16 bg-white rounded-2xl animate-pulse border border-gray-100" />
              ))}
            </div>
          ) : tickets.length === 0 ? (
            <div className="bg-white border border-gray-100 rounded-3xl p-12 shadow-sm text-center">
              <div className="w-14 h-14 rounded-2xl bg-gray-100 flex items-center justify-center mx-auto mb-4">
                <Inbox className="w-7 h-7 text-gray-400" />
              </div>
              <p className="text-gray-700 font-black uppercase tracking-tight">No tickets match the current filters</p>
            </div>
          ) : (
            <div className="bg-white border border-gray-100 rounded-2xl shadow-sm overflow-hidden">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-gray-50 bg-gray-50/60">
                    <th className="text-left text-[9px] font-black text-gray-400 uppercase tracking-widest px-5 py-3">Ref / Subject</th>
                    <th className="text-left text-[9px] font-black text-gray-400 uppercase tracking-widest px-4 py-3 hidden md:table-cell">Member</th>
                    <th className="text-left text-[9px] font-black text-gray-400 uppercase tracking-widest px-4 py-3 hidden lg:table-cell">Category</th>
                    <th className="text-left text-[9px] font-black text-gray-400 uppercase tracking-widest px-4 py-3">Priority</th>
                    <th className="text-left text-[9px] font-black text-gray-400 uppercase tracking-widest px-4 py-3">Status</th>
                    <th className="text-left text-[9px] font-black text-gray-400 uppercase tracking-widest px-4 py-3 hidden sm:table-cell">Date</th>
                    <th className="px-4 py-3" />
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-50">
                  {tickets.map((t) => (
                    <tr key={t.id} className="hover:bg-gray-50/60 transition-colors">
                      <td className="px-5 py-4">
                        <div className="text-[10px] font-black text-gray-400 uppercase tracking-wider">{t.reference}</div>
                        <div className="text-sm font-black text-gray-900 truncate max-w-[200px]">{t.subject}</div>
                      </td>
                      <td className="px-4 py-4 text-sm text-gray-600 hidden md:table-cell">{t.user?.name ?? "—"}</td>
                      <td className="px-4 py-4 text-xs text-gray-500 hidden lg:table-cell">{t.category?.name ?? "—"}</td>
                      <td className="px-4 py-4"><PriorityBadge priority={t.priority} /></td>
                      <td className="px-4 py-4"><StatusBadge status={t.status} /></td>
                      <td className="px-4 py-4 text-xs text-gray-400 hidden sm:table-cell">{fmt(t.created_at)}</td>
                      <td className="px-4 py-4">
                        <Link
                          to={`/admin/support/tickets/${t.id}`}
                          className="px-3 py-1.5 bg-indigo-50 text-indigo-600 text-xs font-black rounded-xl hover:bg-indigo-100 transition-colors"
                        >
                          View
                        </Link>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex items-center justify-center gap-3">
              <button
                disabled={page <= 1}
                onClick={() => setPage((p) => p - 1)}
                className="px-4 py-2 text-sm font-black border border-gray-200 rounded-xl bg-white hover:bg-gray-50 disabled:opacity-40 transition"
              >
                Previous
              </button>
              <span className="text-sm text-gray-600 font-black">
                Page {page} of {totalPages}
                <span className="text-gray-400 font-normal ml-1">({total} total)</span>
              </span>
              <button
                disabled={page >= totalPages}
                onClick={() => setPage((p) => p + 1)}
                className="px-4 py-2 text-sm font-black border border-gray-200 rounded-xl bg-white hover:bg-gray-50 disabled:opacity-40 transition"
              >
                Next
              </button>
            </div>
          )}

        </div>
      </div>
    </PageGate>
  );
}
